import { IconGoogle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { ReadonlyField } from "@/features/user-setting/components/detail/ReadonlyField";
import { text } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import { FC, useState } from "react";

type TExternalLoginSectionProps = {
  connected?: boolean;
  connectedEmail?: string | null;
  onDisconnect?: () => void;
  // Start the Google link flow (redirects to Cognito). Shown when not yet connected.
  onConnect?: () => void;
  // Disable the connect/disconnect actions (used on the edit screen; manage from Detail).
  disabled?: boolean;
};

export const ExternalLoginSection: FC<TExternalLoginSectionProps> = ({
  connected = false,
  connectedEmail,
  onDisconnect,
  onConnect,
  disabled = false,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleConfirm = () => {
    onDisconnect?.();
    setIsConfirmOpen(false);
  };

  return (
    <Stack gap="16px">
      <Typography variant="sub16Semi" color={text.primary}>
        外部サービスによるログイン
      </Typography>

      <Stack direction="row" gap="24px" alignItems="flex-start" flexWrap="wrap">
        <Stack direction="row" gap="48px" flex={1} minWidth={0} flexWrap="wrap">
          <ReadonlyField label="接続ステータス" value={connected ? "Googleログインが有効" : "未接続"} />
          <ReadonlyField label="接続先メールアドレス" value={connected ? connectedEmail : "-"} />
        </Stack>
        {connected ? (
          <Button
            size="sm"
            variant="danger"
            disabled={disabled}
            onClick={() => setIsConfirmOpen(true)}
            // Only tint red when enabled; when disabled, defer to the Button's disabled styling.
            sx={
              disabled
                ? undefined
                : {
                    "&.MuiButtonBase-root": {
                      color: text.error_light,
                      borderColor: text.error_light,
                    },
                  }
            }
          >
            ログイン接続を切断
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={disabled} onClick={onConnect}>
            <Stack direction="row" gap={1} alignItems="center">
              <IconGoogle fontSize={20} style={{ filter: disabled ? "grayscale(100%)" : "none" }} />
              <Typography variant="body14Semi">Googleで登録</Typography>
            </Stack>
          </Button>
        )}
      </Stack>

      <Modal
        isOpen={isConfirmOpen}
        title="ログイン接続の切断"
        labelOk="切断する"
        labelClose="キャンセル"
        onOk={handleConfirm}
        onClose={() => setIsConfirmOpen(false)}
      >
        <Stack gap="12px" py="8px" mb="16px" width={360}>
          <Typography variant="body14" color={text.secondary}>
            Googleログインとの接続を切断します。切断後は、メールアドレスとパスワードでのみログインできます。よろしいですか？
          </Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};
